<template>
  <div class="user-management">
    <div class="page-header">
      <h2>用户管理</h2>
    </div>
    
    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchText"
        placeholder="搜索用户名或昵称"
        style="width: 300px; margin-right: 10px;"
        clearable
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button @click="fetchUsers">搜索</el-button>
    </div>
    
    <!-- 用户列表 -->
    <div class="user-list">
      <el-table 
        :data="filteredUsers" 
        style="width: 100%"
        v-loading="loading"
      >
        <el-table-column prop="id" label="用户ID" width="80" />
        <el-table-column prop="username" label="用户名" width="150" />
        <el-table-column prop="nickname" label="昵称" width="150" />
        <el-table-column prop="realName" label="真实姓名" width="150" />
        <el-table-column prop="role" label="角色" width="120">
          <template #default="scope">
            <el-tag 
              :type="((scope.row.role || '') === 'admin' || (scope.row.role || '') === 'ADMIN') ? 'danger' : 'primary'"
            >
              {{ getRoleText(scope.row.role || '') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="120">
          <template #default="scope">
            <el-tag 
              :type="getStatusTagType(scope.row.status || '')"
            >
              {{ getStatusText(scope.row.status || '') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="lastLoginTime" label="最后登录" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.lastLoginTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template #default="scope">
            <el-button 
              size="small" 
              type="primary" 
              @click="editUserRole(scope.row)"
              :disabled="(scope.row.role === 'admin' || scope.row.role === 'ADMIN') && scope.row.id === currentUser?.id"
            >
              编辑权限
            </el-button>
            <el-button 
              v-if="scope.row.status === 'pending' || scope.row.status === 'PENDING'"
              size="small" 
              type="success"
              @click="approveUser(scope.row)"
            >
              启用
            </el-button>
            <el-button 
              v-else
              size="small" 
              :type="(scope.row.status === 'active' || scope.row.status === 'ACTIVE') ? 'warning' : 'success'"
              @click="toggleUserStatus(scope.row)"
              :disabled="(scope.row.role === 'admin' || scope.row.role === 'ADMIN') && scope.row.id === currentUser?.id"
            >
              {{ (scope.row.status === 'active' || scope.row.status === 'ACTIVE') ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 编辑用户权限对话框 -->
    <el-dialog 
      title="编辑用户权限"
      v-model="showEditDialog"
      width="400px"
    >
      <el-form 
        ref="userForm" 
        :model="userForm" 
        label-width="80px"
      >
        <el-form-item label="用户名">
          <el-input v-model="userForm.username" disabled />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="userForm.nickname" disabled />
        </el-form-item>
        <el-form-item label="角色">
          <el-select v-model="userForm.role" placeholder="请选择角色">
            <el-option label="普通用户" value="user" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showEditDialog = false">取消</el-button>
          <el-button type="primary" @click="saveUserRole" :loading="saving">
            保存
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { mapGetters } from 'vuex'
import axios from 'axios'

export default {
  name: 'UserManagement',
  data() {
    return {
      searchText: '',
      loading: false,
      saving: false,
      showEditDialog: false,
      editingUser: null,
      userForm: {
        id: null,
        username: '',
        nickname: '',
        role: ''
      }
    }
  },
  computed: {
    ...mapGetters(['currentUser']),
    filteredUsers() {
      const users = this.$store.state.users || []
      if (!this.searchText) {
        return users
      }
      return users.filter(user => 
        user && user.username && user.username.toLowerCase().includes(this.searchText.toLowerCase()) ||
        (user && user.nickname && user.nickname.toLowerCase().includes(this.searchText.toLowerCase()))
      )
    }
  },
  methods: {
    async fetchUsers() {
      this.loading = true
      try {
        await this.$store.dispatch('fetchUsers')
      } catch (error) {
        ElMessage.error('获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    
    editUserRole(user) {
      this.editingUser = user
      this.userForm = {
        id: user.id,
        username: user.username,
        nickname: user.nickname || '',
        role: user.role
      }
      this.showEditDialog = true
    },
    
    async saveUserRole() {
      try {
        this.saving = true
        
        await axios.put(`/api/admin/users/${this.userForm.id}/role`, {
          role: this.userForm.role
        })
        
        // 更新本地数据
        const userIndex = this.$store.state.users.findIndex(u => u.id === this.userForm.id)
        if (userIndex !== -1) {
          this.$store.state.users[userIndex].role = this.userForm.role
        }
        
        ElMessage.success('用户权限更新成功')
        this.showEditDialog = false
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '更新失败')
      } finally {
        this.saving = false
      }
    },
    
    async approveUser(user) {
      try {
        await ElMessageBox.confirm(
          `确定要启用用户 "${user.username}" 吗？启用后该用户可以借用设备。`,
          '确认启用',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'info'
          }
        )
        
        await axios.put(`/api/admin/users/${user.id}/status`, {
          status: 'active'
        })
        
        // 更新本地数据
        const userIndex = this.$store.state.users.findIndex(u => u.id === user.id)
        if (userIndex !== -1) {
          this.$store.state.users[userIndex].status = 'active'
        }
        
        ElMessage.success('用户启用成功')
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('启用失败')
        }
      }
    },
    
    async toggleUserStatus(user) {
      const isActive = user.status === 'active' || user.status === 'ACTIVE'
      const action = isActive ? '禁用' : '启用'
      const newStatus = isActive ? 'inactive' : 'active'
      
      try {
        await ElMessageBox.confirm(
          `确定要${action}用户 "${user.username}" 吗？`,
          `确认${action}`,
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        await axios.put(`/api/admin/users/${user.id}/status`, {
          status: newStatus
        })
        
        // 更新本地数据
        const userIndex = this.$store.state.users.findIndex(u => u.id === user.id)
        if (userIndex !== -1) {
          this.$store.state.users[userIndex].status = newStatus
        }
        
        ElMessage.success(`用户${action}成功`)
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(`${action}失败`)
        }
      }
    },
    
    getRoleText(role) {
      const roleMap = {
        admin: '管理员',
        user: '普通用户'
      }
      return roleMap[role] || role
    },
    
    getStatusText(status) {
      const statusMap = {
        pending: '待启用',
        PENDING: '待启用',
        active: '正常',
        ACTIVE: '正常',
        inactive: '禁用',
        INACTIVE: '禁用'
      }
      return statusMap[status] || status
    },
    
    getStatusTagType(status) {
      const typeMap = {
        pending: 'warning',
        PENDING: 'warning',
        active: 'success',
        ACTIVE: 'success',
        inactive: 'info',
        INACTIVE: 'info'
      }
      return typeMap[status] || 'info'
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    }
  },
  
  mounted() {
    this.fetchUsers()
  }
}
</script>

<style scoped>
.user-management {
  background: white;
  border-radius: 8px;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.page-header h2 {
  margin: 0;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.user-list {
  margin-top: 20px;
}

:deep(.el-dialog__body) {
  padding: 20px;
}
</style>